/* Verbatim port of aiHeadline from service/review/index.html (~1012-1025).
 *
 * PARAMETERIZED (rule a): reads the global `STATE.aiNarrative` — takes
 * `state: AppPayloadState` as an explicit first parameter.
 */
import type { AppPayloadState } from './state-shape.ts';

export interface AiHeadline {
  /** null = source ownership genuinely not measured this run (see below) */
  ownership: number | null;
  total: number;
  engines: number;
  themes: number;
  negThemes: number;
  unanimousNeg: number;
  discovered: number;
}

/* AI plane */
export function aiHeadline(state: AppPayloadState): AiHeadline | null {
  const ai = state.aiNarrative; if (!ai) return null;
  const doms = ai.citedDomains || [];
  const total = doms.reduce((s,d) => s + d.count, 0);
  const kps = ai.keyPoints || [];
  const negThemes = kps.filter((k) => k.negative);
  // "unanimous" means every engine — engineCount must equal the tracked set
  const engines = (ai.engines||[]).length;
  const unanimousNeg = negThemes.filter((k) => engines && k.engineCount >= engines);
  // HONESTY (coverage-honesty audit P2-1): plane-presence ≠ measurement. An
  // AIQ poll may carry no owned-vs-earned citation split — `sourceOwnership`
  // is then null/absent. The old `|| 0` collapsed that into a confident "0%
  // owned", which then rendered as a measured stat + meter downstream. Mirror
  // the backend contract (run_diff._ai_delta / ws_derive both guard
  // `sourceOwnership is not None`): preserve null when there is no real
  // measured number, so consumers can render the honest "not measured"
  // treatment instead of a fabricated zero.
  //
  // ...AND gate on the citation base (`total`): the backend's source_ownership()
  // returns a real 0.0 when total citations == 0, but a share over an EMPTY base
  // is a ratio with no denominator, not a measurement — "0% of 0 citations" is
  // the same lie in different clothes. A measured 0 over a NON-empty base is a
  // real datapoint and keeps its confident render.
  const ownership: number | null =
    Number.isFinite(ai.sourceOwnership) && total > 0 ? ai.sourceOwnership : null;
  return { ownership, total, engines,
           themes: kps.length, negThemes: negThemes.length, unanimousNeg: unanimousNeg.length,
           discovered: (ai.promotedToAssets||[]).length };
}
